import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { tap, catchError, shareReplay } from 'rxjs/operators';
import { Modulo } from '../../modulos/main/objetos/modulo';
import { SimpleResponse } from '../objetos/simple-response';
import { ConexionesService } from './conexiones.service';
import { environment } from '../../../environments/environment';
import { TemaService } from './tema.service';

const MODULOS_CON_TEMA = /wms|compras/i;

@Injectable({
  providedIn: 'root',
})
export class ModulosUsuarioService {
  private baseUrl = '';

  private _modulos = new BehaviorSubject<Modulo[]>([]);
  public readonly modulosRO: Observable<Modulo[]> =
    this._modulos.asObservable();

  public _favoritos = new BehaviorSubject<Modulo[]>([]);
  public readonly favoritosRO: Observable<Modulo[]> =
    this._favoritos.asObservable();

  private cargaEnCurso$: Observable<SimpleResponse> | null = null;

  constructor(
    private http: HttpClient,
    private conexionesService: ConexionesService,
    private temaService: TemaService,
  ) {
    if (localStorage.getItem('modulos')) {
      this.asignarModulosAll(JSON.parse(localStorage.getItem('modulos') || ''));
    }

    if (localStorage.getItem('favoritos')) {
      this._favoritos.next(
        JSON.parse(localStorage.getItem('favoritos') || '[]'),
      );
    }

    conexionesService.conexionesRO.subscribe((res) => {
      if (res && res.length > 0) {
        const apiNombre = environment.conexiones_nombre.auth;
        const conexion: any = conexionesService.buscarConexion(apiNombre);
        this.baseUrl = conexion?.URL;
      }
    });
  }

  /**
   * Carga los módulos del usuario una sola vez y comparte el resultado
   * entre todos los suscriptores. Si ya hay una petición en curso, reutiliza
   * esa misma petición. Usar `refrescar()` para invalidar el caché.
   */
  cargarModulosUsuario(): Observable<SimpleResponse> {
    if (this._modulos.value.length > 0 && !this.cargaEnCurso$) {
      return of({
        error: false,
        msg: 'cached',
        payload: {
          modulos: this._modulos.value,
          favoritos: this._favoritos.value,
        },
      } as SimpleResponse);
    }

    if (!this.cargaEnCurso$) {
      this.cargaEnCurso$ = this.http
        .get<SimpleResponse>(`${this.baseUrl}/api/modules/usuario`, {
          headers: { 'x-auth': localStorage.getItem('token') || '' },
        })
        .pipe(
          tap((response) => {
            if (!response.error && response.payload?.modulos?.length) {
              this.asignarModulosAll(response.payload.modulos);
              this.asignarFavoritos(response.payload.favoritos);
            }
          }),
          shareReplay(1),
          catchError((err) => {
            this.cargaEnCurso$ = null;
            throw err;
          }),
        );
    }

    return this.cargaEnCurso$;
  }

  refrescar(): void {
    this.cargaEnCurso$ = null;
    this.cargarModulosUsuario().subscribe();
  }

  limpiarCache(): void {
    this.cargaEnCurso$ = null;
    this._modulos.next([]);
    this._favoritos.next([]);
  }

  public asignarModulosAll = (modulos: Modulo[]): void => {
    this._modulos.next(modulos);
    localStorage.setItem('modulos', JSON.stringify(modulos));
    this.temaService.actualizarPermiso(
      modulos.some((m) => MODULOS_CON_TEMA.test(m.url ?? m.nombre ?? '')),
    );
    Promise.resolve().then(() => this.reconciliarTemaConBackend());
  };

  private reconciliarTemaConBackend(): void {
    this.temaService.obtenerTemaBackend().subscribe((temaBackend) => {
      if (temaBackend && temaBackend !== this.temaService.preferencia) {
        this.temaService.establecerPreferencia(temaBackend, false);
      }
    });
  }

  public asignarFavoritos = (favoritos: Modulo[]): void => {
    this._favoritos.next(favoritos);
    localStorage.setItem('favoritos', JSON.stringify(favoritos));
  };
}
